import React, { useState } from 'react';
import { FaWhatsapp, FaChevronRight, FaLeaf, FaCheckCircle, FaTint } from 'react-icons/fa';

// Drip Irrigation Products Data
const dripIrrigationProducts = [
  {
    id: 'ganga-il',
    name: 'Ganga IL (Inline Lateral)',
    image: '/ganga-il.png',
    overview: 'A flexible lateral pipe with drippers that can be integrated into the pipe. This system is designed for uniform water distribution in closely spaced crops.',
    applications: ['Row crops', 'Vegetables', 'Sugarcane', 'Closely spaced field crops'],
    specifications: [
      { label: 'Sizes', value: '12mm, 16mm, 20mm, 32mm' },
      { label: 'Type', value: 'Inline emitter' },
      { label: 'Class', value: 'ISI Class 2' }
    ]
  },
  {
    id: 'ganga-ol',
    name: 'Ganga OL (Online Lateral)',
    image: '/ganga-ol.png',
    overview: 'A flexible lateral pipe designed for uniform water distribution in closely spaced crops. Compatible with online drippers for versatile irrigation setups.',
    applications: ['Orchards', 'Vineyards', 'Fruit plantations', 'Garden irrigation'],
    specifications: [
      { label: 'Sizes', value: '12mm, 16mm, 20mm, 32mm' },
      { label: 'Type', value: 'Plain lateral (online system)' }
    ]
  },
  {
    id: 'ganga-dripper',
    name: 'Ganga Dripper (Online Dripper)',
    image: '/ganga-dripper.png',
    overview: 'A precision emitter that can be punched into the lateral pipes for targeted water delivery directly to plant roots.',
    applications: ['Fruit plants', 'Coconut & Pomegranate', 'Nursery plants', 'Garden irrigation'],
    specifications: [
      { label: 'Discharge', value: '4 LPH' },
      { label: 'Type', value: 'Online dripper' },
      { label: 'Material', value: 'UV-stabilized plastic' }
    ]
  },
  {
    id: 'ganga-filter',
    name: 'Ganga Filter System',
    image: '/ganga-filter.png',
    overview: 'Ensures clean water supply by removing particles, essential for maintaining a complete drip irrigation system and preventing clogging.',
    applications: ['All drip irrigation systems', 'Borewell water filtration', 'Canal water filtration'],
    specifications: [
      { label: 'Screen Filter (Metal/HDPE)', value: '20 m\u00B3/hr (2"), 30 m\u00B3/hr (2.5"), 40 m\u00B3/hr (3")' },
      { label: 'Sand (Gravel) Filter', value: '10 m\u00B3/hr (2"), 30 m\u00B3/hr (2.5"), 40 m\u00B3/hr (3")' }
    ]
  },
  {
    id: 'ganga-injector',
    name: 'Ganga Injector (Venturi Fertilizer Injector)',
    image: '/ganga-injector.png',
    overview: 'Allows direct mixing of fertilizers into the irrigation system for efficient fertigation in crops, maximizing nutrient delivery.',
    applications: ['Fertigation in all crops', 'Drip & sprinkler systems', 'Greenhouse farming'],
    specifications: [
      { label: 'Size', value: '2"' },
      { label: 'Material', value: 'Durable plastic' },
      { label: 'Installation', value: 'Easy inline installation' }
    ]
  },
  {
    id: 'ganga-valve',
    name: 'Ganga Valve System',
    image: '/ganga-valve.png',
    overview: 'Controls water supply, pressure, and flushing for smooth irrigation operation and maintenance of your entire system.',
    applications: ['Mainline control', 'Pressure regulation', 'System flushing', 'Maintenance'],
    specifications: [
      { label: 'Types', value: 'Single Ball Valve, Flush Valve, Throttle Valve' },
      { label: 'Sizes', value: '2", 2.5"' }
    ]
  },
  {
    id: 'ganga-fittings',
    name: 'Ganga Fittings & Accessories',
    image: '/ganga-fittings.png',
    overview: 'Essential components for building and maintaining a complete drip irrigation system. Includes all connectors, end caps, and control pieces.',
    applications: ['System installation', 'System flushing', 'Repairs & Maintenance'],
    specifications: [
      { label: 'Joiners', value: '12, 16, 20, 32 mm' },
      { label: 'End Caps', value: '12, 16, 20, 32 mm' },
      { label: 'Take-offs', value: '12, 16, 20, 32 mm' },
      { label: 'Rubber Grommets', value: '12, 20, 32 mm' },
      { label: 'Includes', value: 'Ball Valve, Lateral Cock, Flush Valve, Throttle Valve' }
    ]
  }
];

// Sprinkler Irrigation Products Data
const sprinklerIrrigationProducts = [
  {
    id: 'micro-jet-sprinkler',
    name: 'Micro Jet Sprinkler',
    image: 'https://images.unsplash.com/photo-1622122123829-e0490a288d04?w=400&h=300&fit=crop',
    overview: 'A low-flow, fine-distribution sprinkler designed for use with seedlings and delicate plants. Ideal for precision irrigation in small areas.',
    applications: ['Balcony & terrace gardens', 'Leafy vegetable farming (spinach, coriander, lettuce)', 'Nurseries & polyhouses'],
    specifications: [
      { label: 'Flow Rate', value: '20-120 LPH per nozzle' },
      { label: 'Radius', value: '0.5-2.5 meters' },
      { label: 'Pressure', value: '1.0-2.5 bar' },
      { label: 'Spray Pattern', value: '360\u00B0 / adjustable' },
      { label: 'Material', value: 'UV-stabilized plastic' }
    ],
    kitIncludes: ['4mm/8mm lateral pipes', 'Micro jet sprinkler', 'Connectors (tees, couplers)', 'Take-off connectors', 'End caps', 'Riser pipes/spikes']
  },
  {
    id: 'mini-sprinkler',
    name: 'Mini Sprinkler',
    image: 'https://images.unsplash.com/photo-1770664945615-52203ab54c88?w=400&h=300&fit=crop',
    overview: 'A medium-coverage sprinkler suitable for home gardens, lawns, and vegetable plots. Provides reliable and even water distribution.',
    applications: ['Home gardens & lawns', 'Vegetable farming', 'Landscaping areas', 'Small orchards'],
    specifications: [
      { label: 'Flow Rate', value: '20-120 LPH' },
      { label: 'Radius', value: '0.5-2.5 meters' },
      { label: 'Pressure', value: '1.5-3 bar' },
      { label: 'Spray Pattern', value: '360\u00B0 / part circle' }
    ],
    kitIncludes: ['HDPE pipes', 'Lateral pipes (12mm/16mm)', 'Connectors & fittings', 'Riser pipes/spikes']
  },
  {
    id: 'standard-sprinkler',
    name: 'Standard Sprinkler',
    image: 'https://images.unsplash.com/photo-1771684512143-88bdb34782fa?w=400&h=300&fit=crop',
    overview: 'A robust sprinkler designed for field irrigation and larger landscape areas. Delivers consistent coverage for medium-sized farms.',
    applications: ['Agricultural fields', 'Lawns & landscapes', 'Fodder crops', 'Medium-sized farms'],
    specifications: [
      { label: 'Flow Rate', value: '100-300 LPH' },
      { label: 'Radius', value: '2-5 meters' },
      { label: 'Pressure', value: '1.5-3 bar' }
    ],
    kitIncludes: ['HDPE main pipe', 'Couplers & fittings', 'Control valve', 'Filter unit', 'Tripod/riser (optional)']
  },
  {
    id: 'rain-gun',
    name: 'Rain Gun',
    image: 'https://images.unsplash.com/photo-1589515666067-b39eb31af3ac?w=400&h=300&fit=crop',
    overview: 'A high-throw sprinkler suitable for large farms and industrial applications. Covers vast areas with powerful, adjustable spray.',
    applications: ['Large farms', 'Sugarcane & field crops', 'Dust suppression', 'Industrial use'],
    specifications: [
      { label: 'Flow Rate', value: '3,000-24,000 LPH' },
      { label: 'Radius', value: '20-50 meters' },
      { label: 'Pressure', value: '3-7 bar' },
      { label: 'Type', value: 'Impact / Rotating' },
      { label: 'Nozzle Size', value: '10-30 mm' },
      { label: 'Rotation', value: 'Full / Part circle' },
      { label: 'Mount', value: 'Spike / Riser' },
      { label: 'Material', value: 'Engineering plastic / Metal' }
    ],
    kitIncludes: ['Rain gun unit', 'Tripod stand', 'High-pressure tubing', 'Nozzle set', 'Connectors & clamps', 'Basic filter (optional)']
  },
  {
    id: 'mister',
    name: 'Mister (Mist System)',
    image: 'https://images.unsplash.com/photo-1730636903572-54c2c12f0ec6?w=400&h=300&fit=crop',
    overview: 'A fine mist system designed for cooling, humidity control, and delicate irrigation. Creates optimal microclimate conditions.',
    applications: ['Outdoor cooling', 'Greenhouses', 'Nurseries', 'Dust suppression'],
    specifications: [
      { label: 'Flow Rate', value: '2-20 LPH per nozzle' },
      { label: 'Pressure', value: '2-5 bar' },
      { label: 'Coverage', value: 'Localized cooling zone' }
    ],
    kitIncludes: ['Mist nozzles', 'Connectors & valves', 'Control valves', 'Hose connector / tap adapter']
  }
];

// Landscape Irrigation Products Data
const landscapeIrrigationProducts = [
  {
    id: 'ganga-landscape-mister',
    name: 'Ganga Mister (Mist System)',
    image: '/landscape-mister.png',
    overview: 'A fine mist system designed for cooling, humidity control, and delicate irrigation in landscape settings.',
    applications: ['Outdoor cooling', 'Greenhouses', 'Nurseries', 'Dust suppression'],
    specifications: [
      { label: 'Flow Rate', value: '2-20 LPH per nozzle' },
      { label: 'Pressure', value: '2-5 bar' },
      { label: 'Coverage', value: 'Localized cooling zone' }
    ],
    kitIncludes: ['Mist nozzles', 'High-pressure tubing', 'Connectors & clamps', 'Control valve', 'Filter unit', 'Hose connector / tap adapter']
  },
  {
    id: 'ganga-newer-joy-sprinkler',
    name: 'Ganga Newer Joy Sprinkler',
    image: '/landscape-joy-sprinkler.png',
    overview: 'Designed for localized watering of plants, flowers, bushes, and vegetables. Features a micro sprinkler head with a flexible arm for precise placement.',
    applications: ['Flower beds', 'Gardens', 'Shrubs', 'Small plantations', 'Roof gardens'],
    specifications: [
      { label: 'Flow Rate', value: '20-120 LPH' },
      { label: 'Radius', value: '0.5-2.5 meters' },
      { label: 'Pressure', value: '1.0-2.5 bar' },
      { label: 'Spray Pattern', value: '360\u00B0 / adjustable' },
      { label: 'Material', value: 'UV-stabilized plastic' }
    ]
  },
  {
    id: 'ganga-new-joy-sprinkler',
    name: 'Ganga New Joy Sprinkler',
    image: '/landscape-joy-sprinkler.png',
    overview: 'A micro sprinkler designed for watering plants, flowers, bushes, and vegetables. Features a micro sprinkler head with a flexible arm for easy positioning.',
    applications: ['Flower beds', 'Gardens', 'Shrubs', 'Small plantations', 'Roof gardens'],
    specifications: [
      { label: 'Flow Rate', value: '20-120 LPH' },
      { label: 'Radius', value: '0.5-2.5 meters' },
      { label: 'Pressure', value: '1.0-2.5 bar' },
      { label: 'Spray Pattern', value: '360\u00B0 / adjustable' },
      { label: 'Material', value: 'UV-stabilized plastic' }
    ]
  },
  {
    id: 'ganga-landscape-rain-gun',
    name: 'Ganga Rain Gun',
    image: '/landscape-rain-gun.png',
    overview: 'A high-impact sprinkler for large farms, sugarcane fields, dust suppression, and industrial use. Covers vast areas with powerful adjustable spray.',
    applications: ['Large farms', 'Sugarcane', 'Dust suppression', 'Industrial use'],
    specifications: [
      { label: 'Flow Rate', value: '3,000-24,000 LPH' },
      { label: 'Radius', value: '20-50 meters' },
      { label: 'Pressure', value: '3-7 bar' }
    ],
    kitIncludes: ['Rain gun unit', 'Tripod stand', 'Nozzle set', 'Optional pump', 'Basic filter (optional)']
  }
];

// HDPE Pipes Categories Data
const hdpePipesCategories = [
  {
    id: 'hdpe-coil-pipes',
    name: 'HDPE Coil Pipes',
    icon: 'coil',
    description: 'High-quality HDPE pipes available in coils for easy transportation and installation.',
    items: [
      '20mm HDPE Pipe',
      '25mm HDPE Pipe',
      '32mm HDPE Pipe',
      '40mm HDPE Pipe',
      '50mm HDPE Pipe',
      '63mm HDPE Pipe'
    ]
  },
  {
    id: 'hdpe-straight-pipes',
    name: 'HDPE Straight Pipes',
    icon: 'straight',
    description: 'Rigid HDPE pipes in straight lengths for main line and sub-main applications.',
    items: [
      '75mm HDPE Pipe',
      '90mm HDPE Pipe',
      '110mm HDPE Pipe',
      '125mm HDPE Pipe',
      '140mm HDPE Pipe',
      '160mm HDPE Pipe'
    ]
  },
  {
    id: 'pressure-ratings',
    name: 'Pressure Ratings Available',
    icon: 'pressure',
    description: 'Wide range of pressure ratings to suit different irrigation requirements.',
    items: [
      '2.5 kg/cm² (PN 2.5)',
      '4 kg/cm² (PN 4)',
      '6 kg/cm² (PN 6)',
      '8 kg/cm² (PN 8)',
      '10 kg/cm² (PN 10)',
      '12.5 kg/cm² (PN 12.5)',
      '16 kg/cm² (PN 16)',
      '20 kg/cm² (PN 20)'
    ]
  },
  {
    id: 'hdpe-fittings',
    name: 'HDPE Pipe Fittings',
    icon: 'fittings',
    description: 'Complete range of compression fittings for HDPE pipe connections.',
    items: [
      'Compression Couplers',
      'Compression Tees',
      'Compression Elbows',
      'Compression End Caps',
      'Reducing Couplers',
      'Male Thread Adapters (MTA)',
      'Female Thread Adapters (FTA)',
      'Saddle Clamps'
    ]
  },
  {
    id: 'hdpe-valves',
    name: 'Valves & Control',
    icon: 'valve',
    description: 'Control valves and accessories for HDPE pipe systems.',
    items: [
      'Ball Valves',
      'Gate Valves',
      'Non-Return Valves',
      'Air Release Valves',
      'Pressure Relief Valves',
      'Foot Valves'
    ]
  },
  {
    id: 'hdpe-accessories',
    name: 'Accessories',
    icon: 'accessories',
    description: 'Supporting accessories for complete HDPE pipe installation.',
    items: [
      'Pipe Clamps & Clips',
      'Wall Brackets',
      'Pipe Cutters',
      'Jointing Tools',
      'Thread Seal Tape',
      'Repair Couplings'
    ]
  }
];

// Economical Irrigation Categories Data
const economicalIrrigationCategories = [
  {
    id: 'drip-tubing-fittings',
    name: 'Drip Tubing & Fittings',
    icon: 'drip-tubing',
    items: [
      'Low Thickness Plain Laterals',
      'Thin Wall Drip Line - Cylindrical',
      'Thin Wall Drip Tape - Flat',
      'Thin Wall Drip Tape Fittings'
    ]
  },
  {
    id: 'vrain-pipe-fittings',
    name: 'V-Rain Pipe & Fittings',
    icon: 'vrain',
    items: [
      'V-Rain Pipe',
      'V-Rain Pipe Fittings'
    ]
  },
  {
    id: 'lay-flat-pipes',
    name: 'Lay Flat Pipes',
    icon: 'layflat',
    items: [
      'Lay Flat Pipe'
    ]
  }
];

// HDPE Pipe Features
const hdpeFeatures = [
  {
    title: '100% Virgin Material',
    description: 'Made from 100% virgin HDPE raw materials for superior quality'
  },
  {
    title: 'Light Weight & Flexible',
    description: 'Lighter than conventional Steel/GI pipes, easy to handle and install'
  },
  {
    title: 'Corrosion Resistant',
    description: 'No rust or corrosion, ideal for underground and above-ground use'
  },
  {
    title: 'UV Stabilized',
    description: 'Can be used underground or on ground with UV protection'
  },
  {
    title: 'High Impact Strength',
    description: 'Excellent resistance to impact and pressure variations'
  },
  {
    title: 'Food Grade Compliance',
    description: 'Safe for potable water applications'
  }
];

// HDPE Pipe Dimension Chart
const hdpeDimensionChart = [
  { od: '20mm', pn25: '1.8', pn4: '1.8', pn6: '2.0', pn8: '2.3', pn10: '2.3' },
  { od: '25mm', pn25: '1.8', pn4: '1.8', pn6: '2.0', pn8: '2.3', pn10: '2.8' },
  { od: '32mm', pn25: '1.8', pn4: '1.8', pn6: '2.0', pn8: '2.9', pn10: '3.6' },
  { od: '40mm', pn25: '1.8', pn4: '1.9', pn6: '2.4', pn8: '3.7', pn10: '4.5' },
  { od: '50mm', pn25: '1.8', pn4: '2.4', pn6: '3.0', pn8: '4.6', pn10: '5.6' },
  { od: '63mm', pn25: '2.0', pn4: '3.0', pn6: '3.8', pn8: '5.8', pn10: '7.1' },
  { od: '75mm', pn25: '2.3', pn4: '3.6', pn6: '4.5', pn8: '6.9', pn10: '8.4' },
  { od: '90mm', pn25: '2.8', pn4: '4.3', pn6: '5.4', pn8: '8.2', pn10: '10.1' },
  { od: '110mm', pn25: '3.4', pn4: '5.3', pn6: '6.6', pn8: '10.0', pn10: '12.3' },
  { od: '125mm', pn25: '3.9', pn4: '6.0', pn6: '7.4', pn8: '11.4', pn10: '14.0' },
  { od: '140mm', pn25: '4.3', pn4: '6.7', pn6: '8.3', pn8: '12.8', pn10: '15.7' },
  { od: '160mm', pn25: '4.9', pn4: '7.7', pn6: '9.5', pn8: '14.6', pn10: '17.9' }
];

// Sidebar categories
const sidebarItems = [
  { id: 'drip-irrigation', name: 'DRIP IRRIGATION' },
  { id: 'sprinkler-irrigation', name: 'SPRINKLER IRRIGATION' },
  { id: 'landscape-irrigation', name: 'LANDSCAPE IRRIGATION' },
  { id: 'economical-irrigation', name: 'ECONOMICAL IRRIGATION' },
  { id: 'hdpe-pipes', name: 'HDPE PIPES' }
];

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('drip-irrigation');

  // WhatsApp number for quotes
  const whatsappNumber = '919513155159';

  const getQuoteLink = (productName) => {
    return `https://wa.me/${whatsappNumber}?text=Hi, I'm interested in ${productName}. Please share the quote and more details.`;
  };

  // Reset filters when switching categories
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  // Get hero image based on category
  const getHeroImage = () => {
    switch(activeCategory) {
      case 'sprinkler-irrigation':
        return 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=1920&h=600&fit=crop';
      case 'landscape-irrigation':
        return 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=1920&h=600&fit=crop';
      case 'hdpe-pipes':
        return 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1920&h=600&fit=crop';
      default:
        return 'https://images.unsplash.com/photo-2132171/pexels-photo-2132171.jpeg?auto=compress&cs=tinysrgb&w=1920';
    }
  };

  // Get page title based on category
  const getPageTitle = () => {
    switch(activeCategory) {
      case 'sprinkler-irrigation':
        return 'Sprinkler Irrigation Products';
      case 'landscape-irrigation':
        return 'Landscape Irrigation Products';
      case 'economical-irrigation':
        return 'Economical Irrigation Products';
      case 'hdpe-pipes':
        return 'HDPE Pipes & Fittings';
      default:
        return 'Drip Irrigation Products';
    }
  };

  // Render drip irrigation section
  const renderDripSection = () => (
    <>
      {/* Products Count */}
      <p className="text-gray-600 mb-6">
        <span className="font-semibold text-[#2E368F]">{dripIrrigationProducts.length}</span> products available
      </p>

      {/* Products Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="products-grid">
        {dripIrrigationProducts.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
            data-testid={`product-card-${product.id}`}
          >
            <div className="md:flex">
              <div className="md:w-2/5 h-56 md:h-auto overflow-hidden bg-gray-50 flex items-center justify-center p-4">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain max-h-64"
                />
              </div>
              <div className="md:w-3/5 p-6">
                <h3 className="text-xl font-bold text-[#2E368F] mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {product.overview}
                </p>

                {/* Applications */}
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Applications</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {product.applications.map((app, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 text-xs text-gray-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full"
                      >
                        <FaCheckCircle className="text-[#6ABD45] text-[9px]" />
                        {app}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Specifications */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Specifications</h4>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <tbody>
                        {product.specifications.map((spec, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-3 py-1.5 font-medium text-[#2E368F] w-1/3">{spec.label}</td>
                            <td className="px-3 py-1.5 text-gray-700">{spec.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <a
                  href={getQuoteLink(product.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-2.5 px-4 rounded-lg transition-all text-sm"
                  data-testid={`get-quote-${product.id}`}
                >
                  <FaWhatsapp className="text-lg" />
                  Get Quote
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  // Render sprinkler irrigation section
  const renderSprinklerSection = () => (
    <>
      {/* Products Count */}
      <p className="text-gray-600 mb-6">
        <span className="font-semibold text-[#2E368F]">{sprinklerIrrigationProducts.length}</span> products available
      </p>

      {/* Products List */}
      <div className="space-y-8" data-testid="sprinkler-products-grid">
        {sprinklerIrrigationProducts.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
            data-testid={`product-card-${product.id}`}
          >
            <div className="md:flex">
              <div className="md:w-1/3 h-64 md:h-auto overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="md:w-2/3 p-6">
                <h3 className="text-2xl font-bold text-[#2E368F] mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {product.overview}
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {/* Applications */}
                  <div>
                    <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Applications</h4>
                    <ul className="space-y-1">
                      {product.applications.map((app, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                          <FaCheckCircle className="text-[#6ABD45] text-xs mt-0.5 flex-shrink-0" />
                          {app}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Specifications */}
                  <div>
                    <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Specifications</h4>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <tbody>
                          {product.specifications.map((spec, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="px-3 py-1.5 font-medium text-[#2E368F]">{spec.label}</td>
                              <td className="px-3 py-1.5 text-gray-700">{spec.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Kit Includes */}
                {product.kitIncludes && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Kit Includes</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {product.kitIncludes.map((item, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center text-xs text-gray-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <a
                  href={getQuoteLink(product.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-2.5 px-6 rounded-lg transition-all text-sm"
                  data-testid={`get-quote-${product.id}`}
                >
                  <FaWhatsapp className="text-lg" />
                  Get Quote
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Benefits Section */}
      <div className="mt-16 bg-white rounded-xl shadow-sm p-8">
        <h3 className="text-2xl font-bold text-[#2E368F] mb-6 text-center">
          Benefits of Sprinkler Irrigation Systems
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FaTint className="text-[#6ABD45]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#2E368F] mb-1">Uniform Distribution</h4>
              <p className="text-gray-600 text-sm">Even water coverage across the entire field</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="text-[#6ABD45]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#2E368F] mb-1">Water Conservation</h4>
              <p className="text-gray-600 text-sm">Efficient water use with minimal wastage</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FaLeaf className="text-[#6ABD45]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#2E368F] mb-1">Cooling & Humidity</h4>
              <p className="text-gray-600 text-sm">Climate control for greenhouses and nurseries</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="text-[#6ABD45]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#2E368F] mb-1">Large Area Coverage</h4>
              <p className="text-gray-600 text-sm">Rain guns cover up to 50 meter radius</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="text-[#6ABD45]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#2E368F] mb-1">Easy Installation</h4>
              <p className="text-gray-600 text-sm">Complete kits with all required components</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="text-[#6ABD45]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#2E368F] mb-1">Versatile Application</h4>
              <p className="text-gray-600 text-sm">From delicate seedlings to large farm fields</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Render landscape irrigation category cards
  // Render landscape irrigation section
  const renderLandscapeSection = () => (
    <>
      {/* Coming Soon Banner */}
      <div className="bg-white border-2 border-[#6ABD45] text-center py-4 px-6 rounded-xl mb-8" data-testid="landscape-coming-soon">
        <h3 className="text-2xl sm:text-3xl font-bold tracking-wide text-[#2E368F]">COMING SOON</h3>
      </div>

      {/* Products Count */}
      <p className="text-gray-600 mb-6">
        <span className="font-semibold text-[#2E368F]">{landscapeIrrigationProducts.length}</span> products available
      </p>

      {/* Products List */}
      <div className="space-y-8" data-testid="landscape-categories-grid">
        {landscapeIrrigationProducts.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
            data-testid={`product-card-${product.id}`}
          >
            <div className="md:flex">
              <div className="md:w-1/3 h-64 md:h-auto overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="md:w-2/3 p-6">
                <h3 className="text-2xl font-bold text-[#2E368F] mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {product.overview}
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {/* Applications */}
                  <div>
                    <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Applications</h4>
                    <ul className="space-y-1">
                      {product.applications.map((app, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                          <FaCheckCircle className="text-[#6ABD45] text-xs mt-0.5 flex-shrink-0" />
                          {app}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Specifications */}
                  <div>
                    <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Specifications</h4>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <tbody>
                          {product.specifications.map((spec, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="px-3 py-1.5 font-medium text-[#2E368F]">{spec.label}</td>
                              <td className="px-3 py-1.5 text-gray-700">{spec.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Kit Includes */}
                {product.kitIncludes && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-[#2E368F] uppercase tracking-wider mb-2">Kit Includes</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {product.kitIncludes.map((item, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center text-xs text-gray-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <a
                  href={getQuoteLink(product.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-2.5 px-6 rounded-lg transition-all text-sm"
                  data-testid={`get-quote-${product.id}`}
                >
                  <FaWhatsapp className="text-lg" />
                  Get Quote
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  // Render HDPE Pipes section
  const renderHdpePipesSection = () => {
    // Icon mapping for HDPE categories
    const getHdpeIcon = (iconType) => {
      switch(iconType) {
        case 'coil':
          return (
            <svg className="w-10 h-10 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth={1.5} />
              <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth={1.5} />
              <circle cx="12" cy="12" r="1" fill="currentColor" />
            </svg>
          );
        case 'straight':
          return (
            <svg className="w-10 h-10 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="8" width="20" height="8" rx="2" stroke="currentColor" strokeWidth={1.5} />
              <line x1="6" y1="8" x2="6" y2="16" stroke="currentColor" strokeWidth={1} />
              <line x1="10" y1="8" x2="10" y2="16" stroke="currentColor" strokeWidth={1} />
              <line x1="14" y1="8" x2="14" y2="16" stroke="currentColor" strokeWidth={1} />
              <line x1="18" y1="8" x2="18" y2="16" stroke="currentColor" strokeWidth={1} />
            </svg>
          );
        case 'pressure':
          return (
            <svg className="w-10 h-10 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={1.5} />
              <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
              <path d="M8 4l1 2M16 4l-1 2M4 8l2 1M20 8l-2 1" stroke="currentColor" strokeWidth={1} />
            </svg>
          );
        case 'fittings':
          return (
            <svg className="w-10 h-10 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M4 12h6M14 12h6M12 4v6M12 14v6" strokeWidth={1.5} strokeLinecap="round" />
              <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth={1.5} />
            </svg>
          );
        case 'valve':
          return (
            <svg className="w-10 h-10 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.5} />
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeWidth={1.5} strokeLinecap="round" />
            </svg>
          );
        case 'accessories':
          return (
            <svg className="w-10 h-10 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth={1.5} />
            </svg>
          );
        default:
          return <FaTint className="text-[#6ABD45] text-3xl" />;
      }
    };

    return (
      <>
        {/* Features Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h3 className="text-xl font-bold text-[#2E368F] mb-4">Why Choose Our HDPE Pipes?</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {hdpeFeatures.map((feature, index) => (
              <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                <FaCheckCircle className="text-[#6ABD45] text-xl mx-auto mb-2" />
                <h4 className="font-semibold text-[#2E368F] text-sm mb-1">{feature.title}</h4>
                <p className="text-gray-600 text-xs">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="hdpe-categories-grid">
          {hdpePipesCategories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-xl border-2 border-dashed border-[#2E368F]/30 p-6 hover:border-[#2E368F] hover:shadow-lg transition-all"
              data-testid={`hdpe-category-${category.id}`}
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="flex-shrink-0">
                  {getHdpeIcon(category.icon)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#2E368F]">
                    {category.name}
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">{category.description}</p>
                </div>
              </div>
              <ul className="space-y-2 ml-2 mb-4">
                {category.items.map((item, index) => (
                  <li key={index} className="text-gray-700 text-sm flex items-start gap-2">
                    <span className="text-[#6ABD45] mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`https://wa.me/${whatsappNumber}?text=Hi, I'm interested in ${category.name}. Please share more details and pricing.`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#6ABD45] font-semibold hover:text-[#5aa33d] transition-colors"
              >
                <FaWhatsapp className="text-lg" />
                <span>Get Quote</span>
              </a>
            </div>
          ))}
        </div>

        {/* Dimension Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-xl font-bold text-[#2E368F] mb-2">HDPE Pipe Dimension Chart</h3>
          <p className="text-gray-600 text-sm mb-4">Wall thickness (mm) for different pressure ratings</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#2E368F] text-white">
                  <th className="px-4 py-3 text-left rounded-tl-lg">OD (mm)</th>
                  <th className="px-4 py-3 text-center">PN 2.5</th>
                  <th className="px-4 py-3 text-center">PN 4</th>
                  <th className="px-4 py-3 text-center">PN 6</th>
                  <th className="px-4 py-3 text-center">PN 8</th>
                  <th className="px-4 py-3 text-center rounded-tr-lg">PN 10</th>
                </tr>
              </thead>
              <tbody>
                {hdpeDimensionChart.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-2 font-semibold text-[#2E368F]">{row.od}</td>
                    <td className="px-4 py-2 text-center text-gray-700">{row.pn25}</td>
                    <td className="px-4 py-2 text-center text-gray-700">{row.pn4}</td>
                    <td className="px-4 py-2 text-center text-gray-700">{row.pn6}</td>
                    <td className="px-4 py-2 text-center text-gray-700">{row.pn8}</td>
                    <td className="px-4 py-2 text-center text-gray-700">{row.pn10}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-500 text-xs mt-4 italic">* Customised 3-layer product available for specific applications on request</p>
        </div>
      </>
    );
  };

  // Render Economical Irrigation section
  const renderEconomicalSection = () => {
    // Icon mapping for economical irrigation categories
    const getEconomicalIcon = (iconType) => {
      switch(iconType) {
        case 'drip-tubing':
          return (
            <svg className="w-12 h-12 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="4" rx="1" stroke="currentColor" strokeWidth={1.5} />
              <line x1="8" y1="8" x2="8" y2="14" stroke="currentColor" strokeWidth={1.5} />
              <line x1="12" y1="8" x2="12" y2="14" stroke="currentColor" strokeWidth={1.5} />
              <line x1="16" y1="8" x2="16" y2="14" stroke="currentColor" strokeWidth={1.5} />
              <ellipse cx="8" cy="16" rx="2" ry="3" stroke="currentColor" strokeWidth={1.5} />
              <ellipse cx="12" cy="16" rx="2" ry="3" stroke="currentColor" strokeWidth={1.5} />
              <ellipse cx="16" cy="16" rx="2" ry="3" stroke="currentColor" strokeWidth={1.5} />
            </svg>
          );
        case 'vrain':
          return (
            <svg className="w-12 h-12 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="10" y="2" width="4" height="8" rx="1" stroke="currentColor" strokeWidth={1.5} />
              <path d="M12 10v4" stroke="currentColor" strokeWidth={1.5} />
              <path d="M8 14h8" stroke="currentColor" strokeWidth={1.5} />
              <rect x="6" y="14" width="4" height="6" rx="1" stroke="currentColor" strokeWidth={1.5} />
              <rect x="14" y="14" width="4" height="6" rx="1" stroke="currentColor" strokeWidth={1.5} />
            </svg>
          );
        case 'layflat':
          return (
            <svg className="w-12 h-12 text-[#6ABD45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <ellipse cx="6" cy="12" rx="4" ry="6" stroke="currentColor" strokeWidth={1.5} />
              <ellipse cx="12" cy="12" rx="3" ry="5" stroke="currentColor" strokeWidth={1.5} />
              <ellipse cx="17" cy="12" rx="2" ry="4" stroke="currentColor" strokeWidth={1.5} />
              <path d="M10 12h2M15 12h2" stroke="currentColor" strokeWidth={1} />
            </svg>
          );
        default:
          return <FaTint className="text-[#6ABD45] text-3xl" />;
      }
    };

    return (
      <>
        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="economical-categories-grid">
          {economicalIrrigationCategories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-xl border-2 border-dashed border-[#6ABD45]/50 p-8 hover:border-[#6ABD45] hover:shadow-lg transition-all"
              data-testid={`economical-category-${category.id}`}
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0">
                  {getEconomicalIcon(category.icon)}
                </div>
                <h3 className="text-xl font-bold text-[#2E368F] pt-2">
                  {category.name}
                </h3>
              </div>
              <ul className="space-y-3 ml-2 mb-6">
                {category.items.map((item, index) => (
                  <li key={index} className="text-gray-700 flex items-start gap-2">
                    <span className="text-[#6ABD45] mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`https://wa.me/${whatsappNumber}?text=Hi, I'm interested in ${category.name} for Economical Irrigation. Please share more details.`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#6ABD45] font-semibold hover:text-[#5aa33d] transition-colors"
              >
                <FaWhatsapp className="text-lg" />
                <span>Enquire Now</span>
              </a>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="mt-12 bg-white rounded-xl shadow-sm p-8">
          <h3 className="text-2xl font-bold text-[#2E368F] mb-6 text-center">
            Benefits of Economical Irrigation
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-[#6ABD45]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-[#6ABD45] text-2xl" />
              </div>
              <h4 className="font-semibold text-[#2E368F] mb-2">Cost Effective</h4>
              <p className="text-gray-600 text-sm">Budget-friendly solutions without compromising on quality</p>
            </div>
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-[#6ABD45]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-[#6ABD45] text-2xl" />
              </div>
              <h4 className="font-semibold text-[#2E368F] mb-2">Easy Installation</h4>
              <p className="text-gray-600 text-sm">Simple setup that reduces labor costs and time</p>
            </div>
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-[#6ABD45]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-[#6ABD45] text-2xl" />
              </div>
              <h4 className="font-semibold text-[#2E368F] mb-2">Water Savings</h4>
              <p className="text-gray-600 text-sm">Efficient water delivery for maximum conservation</p>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Render coming soon for other categories
  const renderComingSoon = () => (
    <div className="text-center py-20 bg-white rounded-xl shadow-sm">
      <FaLeaf className="text-6xl text-[#6ABD45] mx-auto mb-6" />
      <h3 className="text-2xl font-bold text-[#2E368F] mb-4">Coming Soon</h3>
      <p className="text-gray-600 max-w-md mx-auto mb-8">
        We're adding more products to this category. Contact us for immediate assistance with your irrigation needs.
      </p>
      <a
        href={`https://wa.me/${whatsappNumber}?text=Hi, I'm interested in ${getPageTitle()}. Please share more details.`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-3 px-6 rounded-lg transition-all"
      >
        <FaWhatsapp className="text-xl" />
        Contact Us on WhatsApp
      </a>
    </div>
  );

  return (
    <div className="min-h-screen pt-20" data-testid="products-page">
      {/* Hero Section */}
      <section className="relative h-[350px] md:h-[450px]" data-testid="products-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${getHeroImage()})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="max-w-xl">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                  Our Products
                </h1>
                <p className="text-xl text-white/90">
                  Comprehensive range of high-quality irrigation products for all your farming needs
                </p>
              </div>
              
              {/* Sidebar Menu */}
              <div className="hidden md:block bg-white rounded-lg shadow-xl overflow-hidden min-w-[280px]" data-testid="sidebar-menu">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleCategoryChange(item.id)}
                    className={`w-full px-6 py-4 text-left font-semibold transition-all flex items-center justify-between ${
                      activeCategory === item.id
                        ? 'bg-[#6ABD45] text-white'
                        : 'bg-white text-[#2E368F] hover:bg-gray-50'
                    }`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <span>{item.name}</span>
                    <FaChevronRight className={`text-sm ${activeCategory === item.id ? 'text-white' : 'text-gray-400'}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Category Tabs */}
      <div className="md:hidden bg-white border-b overflow-x-auto" data-testid="mobile-tabs">
        <div className="flex">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleCategoryChange(item.id)}
              className={`px-4 py-3 whitespace-nowrap text-sm font-semibold transition-all border-b-2 ${
                activeCategory === item.id
                  ? 'border-[#6ABD45] text-[#6ABD45]'
                  : 'border-transparent text-gray-600 hover:text-[#2E368F]'
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products Section */}
      <section className="py-12 bg-gray-50" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <FaLeaf className="text-[#6ABD45] text-2xl" />
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2E368F]">
                {getPageTitle()}
              </h2>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {activeCategory === 'sprinkler-irrigation' 
                ? 'Micro jets, mini sprinklers, standard sprinklers, rain guns and mist systems for all farming needs'
                : activeCategory === 'landscape-irrigation'
                ? 'Mist systems, joy sprinklers, and rain guns for gardens, flower beds, and large landscape areas'
                : activeCategory === 'hdpe-pipes'
                ? 'High-quality HDPE pipes made from 100% virgin materials with wide range of sizes and pressure ratings'
                : activeCategory === 'economical-irrigation'
                ? 'Budget-friendly irrigation solutions for cost-effective farming without compromising quality'
                : 'Inline laterals, online drippers, filters, injectors, valves and complete fittings for efficient crop watering'}
            </p>
          </div>

          {/* Render appropriate section based on category */}
          {activeCategory === 'drip-irrigation' && renderDripSection()}
          {activeCategory === 'sprinkler-irrigation' && renderSprinklerSection()}
          {activeCategory === 'landscape-irrigation' && renderLandscapeSection()}
          {activeCategory === 'hdpe-pipes' && renderHdpePipesSection()}
          {activeCategory === 'economical-irrigation' && renderEconomicalSection()}
        </div>
      </section>

      {/* Info Section - Show only for drip */}
      {activeCategory === 'drip-irrigation' && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#2E368F] mb-6">
                  Why Choose Ganga Irrigation Drip Products?
                </h2>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaCheckCircle className="text-[#6ABD45] text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2E368F] mb-1">Water Efficiency</h3>
                      <p className="text-gray-600 text-sm">Save up to 60% water compared to traditional irrigation methods</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaCheckCircle className="text-[#6ABD45] text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2E368F] mb-1">Higher Yields</h3>
                      <p className="text-gray-600 text-sm">Uniform water distribution leads to consistent crop growth</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaCheckCircle className="text-[#6ABD45] text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2E368F] mb-1">Long Lasting</h3>
                      <p className="text-gray-600 text-sm">UV stabilized products with 5-10+ years of reliable performance</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#6ABD45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaCheckCircle className="text-[#6ABD45] text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2E368F] mb-1">Easy Installation</h3>
                      <p className="text-gray-600 text-sm">Simple setup with expert guidance and after-sales support</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=400&fit=crop"
                  alt="Drip Irrigation in Field"
                  className="rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#2E368F]" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
            Need Help Choosing the Right Product?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Our irrigation experts are here to help you select the perfect system for your farm.
          </p>
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-full px-8 py-4 font-semibold transition-all text-lg"
            data-testid="expert-help-button"
          >
            <FaWhatsapp className="text-2xl" />
            Chat with Expert on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default Products;
